import { DefaultValues, FieldValues } from 'react-hook-form'

import { FilterSectionBlock } from './components/FilterSectionBlock'
import { FilterSkeletons } from './components/FilterSkeletons'
import { TabFiltersProps } from './filter.types'
import { FilterActions } from './FilterActions'
import { useFilterForm } from './hooks/useFilterForm'
import { useSaveFilterModal } from './hooks/useSaveFilterModal'
import { useUrlLengthCheck } from './hooks/useUrlLengthCheck'
import { SaveFilterModal } from '@components/Filters/SaveFilterModal'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { Form } from '@ui/Form'

export const leftSideGroupStyle = 'rounded-tr-none inline w-[50%] rounded-br-none  mr-0'
export const rightSideGroupStyle = 'rounded-tl-none inline w-[50%] rounded-bl-none ml-0'

interface FilterFormProps<T extends FieldValues, Q> {
  filtersConfig: TabFiltersProps<T, Q>['filtersConfig']
  onFinish: (data: T) => void
  resetFilters: () => void
  initValues?: DefaultValues<T>
  pathname?: string
}

export function FilterForm<T extends FieldValues, Q>({
  onFinish,
  filtersConfig,
  resetFilters,
  initValues,
  pathname,
}: FilterFormProps<T, Q>) {
  const { form, fieldBlocks, sectionNames, showSkeletons, isLoading, filterData, onSubmit, resetFormFilters } =
    useFilterForm({ filtersConfig, onFinish, resetFilters, initValues })

  const urlLengthCheck = useUrlLengthCheck(form, pathname)

  const storageKey = `${filtersConfig.filtersArea}-sections`
  const { valueLS: expandedSections, setValueLS: setExpandedSections } = useLocalStorage<string[]>(
    storageKey,
    sectionNames,
  )

  const {
    isModalOpen,
    filterName,
    setFilterName,
    openModal,
    cancelModal,
    handleSaveFilter,
    handleDeleteFilter,
    savedFilters,
    handleSavedFilterChange,
  } = useSaveFilterModal({ form, filtersArea: filtersConfig.filtersArea, initialValues: filtersConfig.initialValues })

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-1 flex-col'>
          <div className='flex flex-1 flex-col gap-4'>
            {showSkeletons ? (
              <FilterSkeletons count={fieldBlocks.reduce((sum, b) => sum + b.fields.length, 0)} />
            ) : (
              fieldBlocks.map((block, idx) => (
                <FilterSectionBlock
                  key={idx}
                  block={block}
                  control={form.control}
                  expandedSections={expandedSections}
                  setExpandedSections={setExpandedSections}
                />
              ))
            )}
          </div>
          <FilterActions
            isLoading={isLoading}
            filterCount={filterData?.count}
            bulkActions={filterData?.bulk_actions}
            onReset={resetFormFilters}
            form={form}
            filtersArea={filtersConfig.filtersArea}
            savedFilters={savedFilters}
            onOpenSaveModal={openModal}
            onDeleteFilter={handleDeleteFilter}
            onSavedFilterChange={handleSavedFilterChange}
            urlLengthCheck={urlLengthCheck}
          />
        </form>
      </Form>

      <SaveFilterModal
        isOpen={isModalOpen}
        onCancel={cancelModal}
        onSave={handleSaveFilter}
        filterName={filterName}
        setFilterName={setFilterName}
      />
    </>
  )
}
